/**
 * TerraMatrix Design Tokens
 * Centralized color system — Caribbean Blue rebrand
 *
 * This file provides TypeScript constants for all brand colors,
 * ensuring consistency across the application.
 */

// ============================================================================
// PRIMARY BRAND COLORS
// ============================================================================

/**
 * Caribbean Blue - Primary brand color
 * Bright, cyan-leaning sea-blue: airy at top, teal-deep at bottom
 * Usage: Primary buttons, active states, key branding elements
 */
export const CARIBBEAN_BLUE = {
  50:  '#ECFBFE',
  100: '#CEF3FB',
  200: '#A4E8F6',
  300: '#6DD7EE',
  400: '#38C2E0',
  DEFAULT: '#1CA9CC',   // 500 — primary
  500: '#1CA9CC',
  600: '#1389AB',       // pressed / light-mode primary
  700: '#146E8A',
  800: '#155870',
  900: '#134759',
  950: '#0A2E3B',
  hover: '#38C2E0',     // 400
  active: '#1389AB',    // 600
  light: '#6DD7EE',     // 300
  lighter: '#A4E8F6',   // 200
  faint: '#ECFBFE',     // 50
} as const;

// ============================================================================
// NEUTRAL COLORS — INK SCALE
// ============================================================================

/**
 * Ink - Neutral scale (warm-cool balanced slate)
 * Replaces the old CHARCOAL / MID_GRAY / LIGHT_GRAY tokens
 */
export const INK = {
  0:    '#FFFFFF',
  50:   '#F7F8FA',
  100:  '#EEF0F4',
  200:  '#DDE1E8',
  300:  '#BCC2CE',
  400:  '#8E96A6',
  DEFAULT: '#6B7280',   // 500
  500:  '#6B7280',
  600:  '#535A68',
  700:  '#3D434F',
  800:  '#2B2F39',
  900:  '#23262F',      // surface
  950:  '#1C1F26',      // app chrome / page bg in dark
  1000: '#14161C',      // deepest surface
} as const;

// ============================================================================
// SEMANTIC ACCENT COLORS
// ============================================================================

/**
 * Success - Green accent
 */
export const SUCCESS_GREEN = {
  DEFAULT: '#10B981',
  hover: '#059669',
  active: '#047857',
  light: '#34D399',
  lighter: '#6EE7B7',
  faint: '#D1FAE5',
  bg: '#ECFDF5',
  border: '#6EE7B7',
  text: '#047857',
  icon: '#10B981',
} as const;

/**
 * Warning - Amber accent
 */
export const WARNING_AMBER = {
  DEFAULT: '#F59E0B',
  hover: '#D97706',
  active: '#B45309',
  light: '#FBBF24',
  lighter: '#FDE68A',
  faint: '#FEF3C7',
  bg: '#FFFBEB',
  border: '#FDE68A',
  text: '#B45309',
  icon: '#F59E0B',
} as const;

/**
 * Danger - Red accent
 */
export const DANGER_RED = {
  DEFAULT: '#EF4444',
  hover: '#DC2626',
  active: '#B91C1C',
  light: '#F87171',
  lighter: '#FCA5A5',
  faint: '#FEE2E2',
  bg: '#FEF2F2',
  border: '#FCA5A5',
  text: '#B91C1C',
  icon: '#EF4444',
} as const;

/**
 * Info - Uses Caribbean Blue 600
 */
export const INFO_BLUE = {
  DEFAULT: '#1389AB',
  hover: '#146E8A',
  active: '#155870',
  light: '#38C2E0',
  lighter: '#6DD7EE',
  faint: '#ECFBFE',
  bg: '#ECFBFE',
  border: '#A4E8F6',
  text: '#155870',
  icon: '#1389AB',
} as const;

// ============================================================================
// BACKWARD COMPATIBILITY ALIASES (deprecated — migrate to new tokens)
// ============================================================================

/** @deprecated Use CARIBBEAN_BLUE instead */
export const FOREST_GREEN = {
  DEFAULT: CARIBBEAN_BLUE.DEFAULT,
  hover: CARIBBEAN_BLUE.hover,
  active: CARIBBEAN_BLUE.active,
  light: CARIBBEAN_BLUE.light,
  lighter: CARIBBEAN_BLUE.lighter,
  faint: CARIBBEAN_BLUE.faint,
} as const;

/** @deprecated Use INK instead */
export const SAGE_GREEN = {
  DEFAULT: INK[400],
  hover: INK[500],
  active: INK[600],
  light: INK[300],
  lighter: INK[200],
  faint: INK[100],
} as const;

/** @deprecated Use SUCCESS_GREEN instead */
export const SEAFOAM_GREEN = {
  DEFAULT: SUCCESS_GREEN.DEFAULT,
  hover: SUCCESS_GREEN.hover,
  active: SUCCESS_GREEN.active,
  light: SUCCESS_GREEN.light,
  lighter: SUCCESS_GREEN.lighter,
  faint: SUCCESS_GREEN.faint,
} as const;

/** @deprecated Use INFO_BLUE instead */
export const BLUE_GROTTO = {
  DEFAULT: INFO_BLUE.DEFAULT,
  hover: INFO_BLUE.hover,
  active: INFO_BLUE.active,
  light: INFO_BLUE.light,
  lighter: INFO_BLUE.lighter,
  faint: INFO_BLUE.faint,
} as const;

/** @deprecated Use WARNING_AMBER instead */
export const DESERT_SUN = {
  DEFAULT: WARNING_AMBER.DEFAULT,
  hover: WARNING_AMBER.hover,
  active: WARNING_AMBER.active,
  light: WARNING_AMBER.light,
  lighter: WARNING_AMBER.lighter,
  faint: WARNING_AMBER.faint,
} as const;

/** @deprecated Use CARIBBEAN_BLUE[300] or SUCCESS_GREEN.light instead */
export const CHARTREUSE = {
  DEFAULT: '#CCFF00',
  hover: '#b8e600',
  active: '#a3cc00',
  light: '#d6ff33',
  lighter: '#e0ff66',
  faint: '#f5ffe6',
} as const;

/** @deprecated Use INK[950] instead */
export const CHARCOAL = {
  DEFAULT: INK[950],
  alternate: INK[900],
  light: INK[800],
} as const;

/** @deprecated Use INK[500] instead */
export const MID_GRAY = {
  DEFAULT: INK[500],
  light: INK[400],
  lighter: INK[300],
  dark: INK[600],
} as const;

/** @deprecated Use INK[50] instead */
export const LIGHT_GRAY = {
  DEFAULT: INK[50],
  dark: INK[100],
  darker: INK[200],
} as const;

/** @deprecated Use INK[50] or INK[100] instead */
export const CHAMPAGNE = {
  DEFAULT: INK[50],
  dark: INK[100],
  darker: INK[200],
  light: INK[0],
} as const;

/**
 * White - Pure white
 */
export const WHITE = '#FFFFFF';

// ============================================================================
// STATUS & FEEDBACK COLORS
// ============================================================================

export const SUCCESS = {
  DEFAULT: SUCCESS_GREEN.DEFAULT,
  bg: SUCCESS_GREEN.bg,
  border: SUCCESS_GREEN.border,
  text: SUCCESS_GREEN.text,
  icon: SUCCESS_GREEN.icon,
} as const;

export const ERROR = {
  DEFAULT: DANGER_RED.DEFAULT,
  hover: DANGER_RED.hover,
  active: DANGER_RED.active,
  bg: DANGER_RED.bg,
  border: DANGER_RED.border,
  text: DANGER_RED.text,
  icon: DANGER_RED.icon,
} as const;

export const WARNING = {
  DEFAULT: WARNING_AMBER.DEFAULT,
  bg: WARNING_AMBER.bg,
  border: WARNING_AMBER.border,
  text: WARNING_AMBER.text,
  icon: WARNING_AMBER.icon,
} as const;

export const INFO = {
  DEFAULT: INFO_BLUE.DEFAULT,
  bg: INFO_BLUE.bg,
  border: INFO_BLUE.border,
  text: INFO_BLUE.text,
  icon: INFO_BLUE.icon,
} as const;

// ============================================================================
// SEMANTIC TOKENS (for common UI patterns)
// ============================================================================

export const PRIMARY = {
  DEFAULT: CARIBBEAN_BLUE.DEFAULT,
  hover: CARIBBEAN_BLUE.hover,
  active: CARIBBEAN_BLUE.active,
  foreground: WHITE,
} as const;

export const SECONDARY = {
  DEFAULT: INK[200],
  hover: INK[300],
  active: INK[400],
  foreground: INK[900],
} as const;

export const BORDER = {
  DEFAULT: INK[200],
  dark: INK[300],
  light: INK[100],
  input: INK[200],
  focus: CARIBBEAN_BLUE.DEFAULT,
} as const;

export const TEXT = {
  primary: INK[900],
  secondary: INK[500],
  tertiary: INK[400],
  disabled: INK[300],
  inverse: WHITE,
  link: CARIBBEAN_BLUE.DEFAULT,
  linkHover: CARIBBEAN_BLUE.hover,
  placeholder: INK[400],
} as const;

export const BACKGROUND = {
  primary: WHITE,
  secondary: INK[50],
  tertiary: INK[100],
  card: WHITE,
  hover: INK[100],
  overlay: 'rgba(0, 0, 0, 0.5)',
} as const;

// ============================================================================
// DATA VISUALIZATION PALETTE
// ============================================================================

export const CHART_COLORS = [
  CARIBBEAN_BLUE.DEFAULT,   // 1. Caribbean Blue
  '#10B981',                // 2. Emerald
  '#F59E0B',                // 3. Amber
  '#8B5CF6',                // 4. Violet
  '#EC4899',                // 5. Pink
  '#14B8A6',                // 6. Teal
] as const;

export const CHART_COLORS_LIGHT = [
  CARIBBEAN_BLUE.light,
  '#6EE7B7',
  '#FDE68A',
  '#C4B5FD',
  '#F9A8D4',
  '#5EEAD4',
] as const;

// ============================================================================
// SHADOW TOKENS
// ============================================================================

export const SHADOWS = {
  xs: '0 1px 0 rgba(0, 0, 0, 0.4)',
  sm: '0 1px 2px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255,255,255,0.02) inset',
  md: '0 4px 12px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255,255,255,0.02) inset',
  lg: '0 16px 40px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255,255,255,0.03) inset',
  xl: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
  '2xl': '0 50px 100px -20px rgba(0, 0, 0, 0.5)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.3)',
  glow: '0 0 0 4px rgba(56, 194, 224, 0.22)',
  cardHover: '0 4px 12px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255,255,255,0.02) inset',
  dialog: '0 4px 16px rgba(0, 0, 0, 0.4)',
  modal: '0 16px 40px rgba(0, 0, 0, 0.45)',
} as const;

// ============================================================================
// SPACING & RADII
// ============================================================================

/** 4px base grid */
export const SPACING = {
  0:  '0',
  1:  '4px',
  2:  '8px',
  3:  '12px',
  4:  '16px',
  5:  '20px',
  6:  '24px',
  8:  '32px',
  10: '40px',
  12: '48px',
  16: '64px',
  20: '80px',
  24: '96px',
} as const;

export const RADII = {
  xs:   '3px',
  sm:   '4px',
  md:   '6px',
  lg:   '8px',
  xl:   '12px',
  '2xl': '16px',
  full: '999px',
} as const;

// ============================================================================
// EXPORTS
// ============================================================================

export const colors = {
  // New brand colors
  caribbeanBlue: CARIBBEAN_BLUE,
  ink: INK,
  successGreen: SUCCESS_GREEN,
  warningAmber: WARNING_AMBER,
  dangerRed: DANGER_RED,
  infoBlue: INFO_BLUE,

  // Backward-compatible aliases (deprecated)
  forestGreen: FOREST_GREEN,
  sageGreen: SAGE_GREEN,
  seafoamGreen: SEAFOAM_GREEN,
  blueGrotto: BLUE_GROTTO,
  desertSun: DESERT_SUN,
  chartreuse: CHARTREUSE,

  // Neutrals (deprecated aliases)
  charcoal: CHARCOAL,
  midGray: MID_GRAY,
  lightGray: LIGHT_GRAY,
  champagne: CHAMPAGNE,
  white: WHITE,

  // Status
  success: SUCCESS,
  error: ERROR,
  warning: WARNING,
  info: INFO,

  // Semantic
  primary: PRIMARY,
  secondary: SECONDARY,
  border: BORDER,
  text: TEXT,
  background: BACKGROUND,

  // Data visualization
  chart: CHART_COLORS,
  chartLight: CHART_COLORS_LIGHT,

  // Shadows
  shadows: SHADOWS,
} as const;

// Type export for TypeScript consumers
export type Colors = typeof colors;

// Default export
export default colors;
